import { Box, Card, CardContent, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { endOfDay } from 'date-fns'
import React, { useState } from 'react'

import { useStatusLeadTimes, type StatusLeadTime } from '../api/analytics'
import { formatLeadTime } from '../utils/duration.utils'

const shimmerKeyframes = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

const skeletonStyle = {
  display: 'inline-block',
  width: '96px',
  height: '2rem',
  backgroundColor: '#e0e0e0',
  borderRadius: '8px',
  position: 'relative' as const,
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
    animation: 'shimmer 1.5s infinite',
  },
}

const datePickerSlotProps = {
  textField: {
    size: 'small' as const,
    sx: { width: 180 },
  },
}

// One card with the status-change date range and the lead time of both status
// transitions side by side. An unset range covers all appointments.
const StatusLeadTimeCard: React.FC = () => {
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)

  // Format dates for API (RFC3339); the picked to-day is included as a whole
  const { data, isLoading, error } = useStatusLeadTimes(
    fromDate ? fromDate.toISOString() : undefined,
    toDate ? endOfDay(toDate).toISOString() : undefined
  )

  return (
    <>
      <style>{shimmerKeyframes}</style>
      <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}
            >
              Status Change Times
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  slotProps={datePickerSlotProps}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={datePickerSlotProps}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          {error ? (
            <Typography color="error">Failed to load data</Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <LeadTimeSection
                title="Created to Dispatched"
                data={data?.createdToDispatched}
                isLoading={isLoading}
              />
              <LeadTimeSection
                title="Dispatched to Completed"
                data={data?.dispatchedToCompleted}
                isLoading={isLoading}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  )
}

interface LeadTimeSectionProps {
  title: string
  data?: StatusLeadTime
  isLoading: boolean
}

// Average is the headline figure; median, min and max give the spread.
const LeadTimeSection: React.FC<LeadTimeSectionProps> = ({ title, data, isLoading }) => {
  const hasData = !!data && data.count > 0

  return (
    <Box sx={{ flex: '1 1 240px', minWidth: 240 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ height: '2rem', mb: 2, display: 'flex', alignItems: 'center' }}>
        {isLoading ? (
          <Box sx={skeletonStyle} />
        ) : (
          <Typography
            variant="h2"
            sx={{ fontWeight: 700, color: '#2196F3', fontSize: '2rem', lineHeight: 1 }}
          >
            {hasData ? formatLeadTime(data.meanSeconds) : '-'}
          </Typography>
        )}
      </Box>
      <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '1rem', fontWeight: 500 }}>
        Average lead time
      </Typography>
      {!isLoading && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {hasData ? (
            <>
              <LeadTimeRow label="Median" seconds={data.medianSeconds} />
              <LeadTimeRow label="Min" seconds={data.minSeconds} />
              <LeadTimeRow label="Max" seconds={data.maxSeconds} />
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Based on {data.count} appointments
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No appointments made this transition
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

const LeadTimeRow: React.FC<{ label: string; seconds: number }> = ({ label, seconds }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
      {formatLeadTime(seconds)}
    </Typography>
  </Box>
)

export default StatusLeadTimeCard
